/**
 * Shared Event Handlers for Claude Agent Socket Events
 *
 * These handlers are used by both:
 * 1. claudeAgentSocket.ts (critical handlers - persist across unmounts)
 * 2. AgentModeContainer.tsx (component handlers - UI-specific logic)
 *
 * Each handler updates:
 * 1. Zustand store (chatStore) - SOURCE OF TRUTH for running tasks
 * 2. localStorage - browser refresh recovery only
 */

import {
  MessageType,
  TaskStatus,
  ToolStatus,
  ConnectionState,
  ChatResponsePayload,
  ToolStartedPayload,
  ToolCompletedPayload,
  StreamCompletePayload,
  TaskCancelledPayload,
  UserMessagePayload,
  HitlPersistedPayload,
  HitlActionClickedPayload,
  TaskCreatedPayload,
  TaskPausedPayload,
  TaskQueuedPayload,
  AssistantEntrySavedPayload,
  AutoCommitCreatedPayload,
} from "@optidev/shared-types";

import { useAgentChatStore } from "@/stores/agentChatStore";
import { useSlideStore } from "@/stores/slide-store";
import { fetchSlideManifest } from "@/lib/slides/slide-manifest";
import type { ActiveTool, Message } from "@/components/agent/AgentMessage";

// Socket manager reference - set by claudeAgentSocket.ts to break circular dependency
let _claudeSocketManager: { getProjectId: () => string | undefined } | null =
  null;

// Module-level refs for devContainerId and getIdToken — set by editor component
// Used by handleAutoCommitCreated to re-fetch slide manifest
let _devContainerIdRef: string | null = null;
let _getIdTokenRef: (() => Promise<string | null>) | null = null;

// Accumulate dirty slide file paths during active Claude stream.
// Populated by handleToolStarted (Write/Edit on slide files), consumed by handleStreamComplete.
const _pendingDirtySlideFiles = new Set<string>();

// Allow external setter to break circular dependency
// Called by claudeAgentSocket.ts after it initializes
export function setClaudeSocketManager(manager: {
  getProjectId: () => string | undefined;
}) {
  _claudeSocketManager = manager;
}

// Set editor context for event handlers that need devContainerId/getIdToken
export function setEditorContext(ctx: {
  devContainerId: string | null;
  getIdToken: (() => Promise<string | null>) | null;
}): void {
  _devContainerIdRef = ctx.devContainerId;
  _getIdTokenRef = ctx.getIdToken;
}

// Get socket manager (may be null if not yet initialized)
function getClaudeSocketManagerSync() {
  return _claudeSocketManager;
}

// ============================================================
// Store Access (works outside React components)
// ============================================================

const getStore = () => useAgentChatStore.getState();

// ============================================================
// localStorage Helpers
// ============================================================

function getTaskKey(taskId: string): string {
  return `streaming-task-${taskId}`;
}

function getTaskData(taskId: string): Record<string, unknown> | null {
  const key = getTaskKey(taskId);
  const dataStr = localStorage.getItem(key);
  if (!dataStr) return null;

  try {
    return JSON.parse(dataStr);
  } catch {
    return null;
  }
}

function setTaskData(taskId: string, data: Record<string, unknown>): void {
  const key = getTaskKey(taskId);
  localStorage.setItem(key, JSON.stringify(data));
}

function updateLocalStorageContent(taskId: string, content: string): void {
  const taskData = getTaskData(taskId);
  if (!taskData) return;

  // Initialize or update the streaming message
  if (
    !taskData.messages ||
    !Array.isArray(taskData.messages) ||
    taskData.messages.length === 0
  ) {
    taskData.messages = [
      {
        id: taskId,
        type: MessageType.ASSISTANT,
        content: content,
        timestamp: new Date().toISOString(),
        status: TaskStatus.RUNNING,
      },
    ];
  } else {
    // Append content to existing message
    const msg = taskData.messages[0] as Record<string, unknown>;
    msg.content = ((msg.content as string) || "") + content;
  }

  setTaskData(taskId, taskData);
}

function updateLocalStorageTool(taskId: string, tool: ActiveTool): void {
  const taskData = getTaskData(taskId);
  if (!taskData) return;

  // Ensure messages array exists
  if (
    !taskData.messages ||
    !Array.isArray(taskData.messages) ||
    taskData.messages.length === 0
  ) {
    taskData.messages = [
      {
        id: taskId,
        type: MessageType.ASSISTANT,
        content: "",
        timestamp: new Date().toISOString(),
        status: TaskStatus.RUNNING,
        activeTools: [tool],
      },
    ];
  } else {
    const msg = taskData.messages[0] as Record<string, unknown>;
    const activeTools = (msg.activeTools as ActiveTool[]) || [];
    // Avoid duplicates
    if (!activeTools.some((t) => t.toolUseId === tool.toolUseId)) {
      msg.activeTools = [...activeTools, tool];
    }
  }

  setTaskData(taskId, taskData);
}

function updateLocalStorageToolStatus(
  taskId: string,
  toolUseId: string,
  updates: Partial<ActiveTool>,
): void {
  const taskData = getTaskData(taskId);
  if (!taskData) return;

  if (
    taskData.messages &&
    Array.isArray(taskData.messages) &&
    taskData.messages.length > 0
  ) {
    const msg = taskData.messages[0] as Record<string, unknown>;
    const activeTools = msg.activeTools as ActiveTool[] | undefined;
    if (activeTools) {
      const toolIndex = activeTools.findIndex((t) => t.toolUseId === toolUseId);
      if (toolIndex >= 0) {
        activeTools[toolIndex] = { ...activeTools[toolIndex], ...updates };
        msg.activeTools = activeTools;
        setTaskData(taskId, taskData);
      }
    }
  }
}

function markLocalStorageCompleted(taskId: string): void {
  const taskData = getTaskData(taskId);
  if (!taskData) return;

  taskData.status = TaskStatus.COMPLETED;
  taskData.completedAt = new Date().toISOString();

  if (
    taskData.messages &&
    Array.isArray(taskData.messages) &&
    taskData.messages.length > 0
  ) {
    const msg = taskData.messages[0] as Record<string, unknown>;
    msg.status = TaskStatus.COMPLETED;
  }

  setTaskData(taskId, taskData);
}

function removeLocalStorageTask(taskId: string): void {
  const key = getTaskKey(taskId);
  localStorage.removeItem(key);
}

// ============================================================
// Shared Event Handlers
// ============================================================

/**
 * Handle chat-response event (streaming text content)
 * Updates both Zustand store and localStorage
 *
 * This handler now CREATES messages for first chunk and APPENDS for subsequent chunks.
 * Component handlers no longer need to create messages - they only handle UI side effects.
 */
export function handleChatResponse(data: ChatResponsePayload): void {
  // Parse nested data structure if needed
  let parsedData = data;
  if (typeof (data as unknown as { data: string }).data === "string") {
    try {
      parsedData = JSON.parse(
        (data as unknown as { data: string }).data,
      ) as ChatResponsePayload;
    } catch {
      console.log(
        "[AgentEventHandlers] chat-response: Failed to parse nested data",
      );
      return;
    }
  }

  // Only handle streaming text content
  if (parsedData.type !== "stream" || parsedData.data?.type !== "text") {
    return;
  }

  const { sessionId, taskId, userMessageId, parentEntryId } = parsedData;
  const content = parsedData.data?.content || "";

  if (!sessionId || !taskId || !content) return;

  // Filter out abort messages from Claude SDK - these shouldn't appear in chat
  if (
    content.toLowerCase().includes("claude code process aborted") ||
    content.toLowerCase().includes("aborted by user")
  ) {
    console.log("[AgentEventHandlers] Filtering abort message from stream");
    return;
  }

  const store = getStore();
  const messages = store.getMessages(sessionId);

  // Try to find by id first, then by taskId, then by userMessageId
  const lookupUserMessageId = userMessageId || parentEntryId;

  let existingMessage = messages.find((m) => m.id === taskId);
  if (!existingMessage) {
    // Fallback 1: look for message with matching taskId property
    existingMessage = messages.find((m) => m.taskId === taskId);
  }
  if (!existingMessage && lookupUserMessageId) {
    // Fallback 2: look for RUNNING assistant message with same userMessageId
    // This handles post-refresh where API message has different id but same userMessageId
    existingMessage = messages.find(
      (m) =>
        m.type === MessageType.ASSISTANT &&
        m.userMessageId === lookupUserMessageId &&
        (m.status === TaskStatus.RUNNING || m.isLoading),
    );
    if (existingMessage) {
      // Update the message's taskId so future lookups can find it directly
      store.updateMessage(sessionId, existingMessage.id, { taskId });
    }
  }

  if (existingMessage) {
    // Message exists - append content
    // Use taskId for lookup since it's stable (id may change during streaming via assistant-entry-saved)
    store.appendContent(sessionId, taskId, content);
    if (existingMessage.isLoading) {
      store.updateMessage(sessionId, taskId, { isLoading: false });
    }
  } else {
    // First chunk - create message
    // Check for placeholder (isLoading: true)
    const placeholder = messages.find(
      (m) => m.isLoading && m.type === MessageType.ASSISTANT,
    );

    const newMessage: Message = {
      id: taskId,
      taskId, // Persists for lookup even after id becomes assistantMessageId
      entryId: taskId,
      type: MessageType.ASSISTANT,
      content,
      timestamp: new Date(),
      status: TaskStatus.RUNNING,
      isLoading: false,
      userMessageId: userMessageId || parentEntryId || undefined,
      parentEntryId: parentEntryId || undefined,
    };

    if (placeholder) {
      // Replace placeholder with new message
      store.updateMessage(sessionId, placeholder.id, newMessage);
    } else {
      // Add new message
      store.upsertMessage(sessionId, newMessage);
    }

    // NOTE: Do NOT call addActiveTask here
    // task-created is the FIRST event and should be the ONLY place that adds activeTask
    // chat-response should only append content to existing message
  }

  // 2. Update localStorage (browser refresh recovery)
  updateLocalStorageContent(taskId, content);
}

/**
 * Handle tool-started event
 * Updates both Zustand store and localStorage
 * If assistantMessageId is provided, updates message ID from taskId to real database ID
 */
export function handleToolStarted(data: ToolStartedPayload): void {
  const {
    sessionId,
    taskId,
    entryId,
    toolUseId,
    toolName,
    toolInput,
    textPosition,
    assistantMessageId,
  } = data;
  if (!sessionId || !taskId || !toolUseId) return;

  const store = getStore();

  const tool: ActiveTool = {
    entryId: entryId || toolUseId, // Fallback to toolUseId if entryId not available
    toolUseId,
    toolName,
    toolInput,
    textPosition,
    status: ToolStatus.RUNNING,
    startedAt: new Date(),
  };

  // Track Write/Edit on slide files for thumbnail regeneration on stream-complete
  const toolCategory = toolName.toLowerCase();
  if (toolCategory.includes("write") || toolCategory.includes("edit")) {
    const filePath =
      (toolInput?.file_path as string) || (toolInput?.path as string) || "";
    if (
      filePath.includes("/slides/") &&
      (filePath.endsWith(".tsx") || filePath.endsWith(".json"))
    ) {
      _pendingDirtySlideFiles.add(filePath);
    }
  }

  // 1. Update Zustand store - use assistantMessageId if available for finding message
  store.addTool(sessionId, taskId, tool);

  // 2. Update localStorage
  updateLocalStorageTool(taskId, tool);
}

/**
 * Handle tool-completed event
 * Updates both Zustand store and localStorage
 */
export function handleToolCompleted(data: ToolCompletedPayload): void {
  const { sessionId, taskId, toolUseId, isError, resultSummary, errorMessage } =
    data;
  if (!sessionId || !taskId || !toolUseId) return;

  const updates: Partial<ActiveTool> = {
    status: isError ? ToolStatus.ERROR : ToolStatus.COMPLETED,
    resultSummary,
    errorMessage,
  };

  // 1. Update Zustand store
  getStore().updateTool(sessionId, taskId, toolUseId, updates);

  // 2. Update localStorage
  updateLocalStorageToolStatus(taskId, toolUseId, updates);
}

/**
 * Handle stream-complete event
 * Updates both Zustand store and localStorage
 */
export function handleStreamComplete(data: StreamCompletePayload): void {
  const { sessionId, taskId, assistantMessageId, toolUsage } = data;
  if (!sessionId || !taskId) return;

  // 1. Update Zustand store
  // Update both id and entryId to the real database entry ID for proper message merging
  // The taskId property remains unchanged for lookup purposes
  getStore().updateMessage(sessionId, taskId, {
    id: assistantMessageId,
    entryId: assistantMessageId, // Also update entryId for consistency
    status: TaskStatus.COMPLETED,
    toolUsage,
  });
  getStore().removeActiveTask(sessionId, taskId);

  // 2. Update localStorage
  markLocalStorageCompleted(taskId);

  // 3. Regenerate slide thumbnails for files Claude wrote/edited during this stream.
  if (_pendingDirtySlideFiles.size > 0) {
    _pendingDirtySlideFiles.clear();

    // Re-fetch manifest to pick up any new slides added by Claude.
    // Thumbnail capture is handled server-side by auto-commit subscriber →
    // sidecar Puppeteer → slides-thumbnails-updated WebSocket event.
    const devContainerId = _devContainerIdRef;
    const getIdToken = _getIdTokenRef;
    if (devContainerId && getIdToken) {
      fetchSlideManifest({ devContainerId, getIdToken });
    }
  }
}

/**
 * Handle assistant-entry-saved event
 * Updates message ID from taskId to real database entry ID early (before stream-complete)
 * This enables proper merge when user navigates away and back during streaming
 */
export function handleAssistantEntrySaved(
  data: AssistantEntrySavedPayload,
): void {
  const { sessionId, taskId, entryId } = data;
  if (!sessionId || !taskId || !entryId) return;

  // 1. Update Zustand store
  const store = getStore();
  store.updateMessage(sessionId, taskId, {
    id: entryId,
    entryId: entryId,
  });

  // 2. Update localStorage (for browser refresh recovery)
  const taskData = getTaskData(taskId);
  const messages = taskData?.messages as
    | Array<Record<string, unknown>>
    | undefined;
  if (taskData && messages && messages.length > 0) {
    const msg = messages[0];
    msg.id = entryId;
    msg.entryId = entryId;
    setTaskData(taskId, taskData);
  }

  console.log(
    "[AgentEventHandlers] Updated message ID on assistant-entry-saved:",
    taskId,
    "->",
    entryId,
  );
}

/**
 * Handle task-cancelled event
 * Updates both Zustand store and localStorage
 */
export function handleTaskCancelled(data: TaskCancelledPayload): void {
  const { sessionId, taskId, reason } = data;
  if (!sessionId || !taskId) return;

  // 1. Update Zustand store
  // Set isLoading: false to clear the "Thinking..." indicator
  getStore().updateMessage(sessionId, taskId, {
    status: TaskStatus.COMPLETED,
    isLoading: false,
  });
  getStore().removeActiveTask(sessionId, taskId);

  // 2. If connection lost, set error so UI can show banner
  if (reason?.toLowerCase().includes("connection lost")) {
    getStore().setError(sessionId, {
      code: "CONNECTION_LOST",
      message: reason,
    });
  }

  // 3. Cleanup localStorage
  removeLocalStorageTask(taskId);
}

/**
 * Handle user-message event
 * Updates temp ID (msg-X) to real entryId
 * For other tabs: adds user message + placeholder
 */
export function handleUserMessage(data: Omit<UserMessagePayload, "role">): {
  isOtherTab: boolean;
} {
  const { id, userMessageTempId, content, timestamp, sessionId } = data;
  if (!id || !sessionId) return { isOtherTab: false };

  const store = getStore();
  const messages = store.getMessages(sessionId);

  // Check for duplicate
  if (messages.some((m) => m.id === id)) {
    console.log("[AgentEventHandlers] Duplicate user-message, ignoring");
    return { isOtherTab: false };
  }

  // Check if sender's own message (match by userMessageTempId)
  const ownMessage = userMessageTempId
    ? messages.find((m) => m.id === userMessageTempId)
    : null;

  if (ownMessage) {
    // Update temp ID to real ID
    store.updateMessage(sessionId, userMessageTempId!, { id, entryId: id });
    console.log(
      "[AgentEventHandlers] Updated user message temp ID:",
      userMessageTempId,
      "->",
      id,
    );
    return { isOtherTab: false };
  }

  // Other tab's message: add user message + placeholder
  const hasPlaceholder = messages.some(
    (m) => m.type === MessageType.ASSISTANT && m.isLoading,
  );

  store.upsertMessage(sessionId, {
    id,
    entryId: id,
    type: MessageType.USER,
    content,
    timestamp: new Date(timestamp),
  });

  if (!hasPlaceholder) {
    store.upsertMessage(sessionId, {
      id: `placeholder-${id}`,
      type: MessageType.ASSISTANT,
      content: "",
      timestamp: new Date(),
      isLoading: true,
    });
  }

  console.log("[AgentEventHandlers] Added user message from other tab");
  return { isOtherTab: true };
}

/**
 * Handle hitl-persisted event
 * Updates temp ID (hitl-pending-X) to real messageId
 */
export function handleHitlPersisted(data: HitlPersistedPayload): void {
  const { sessionId, taskId, messageId, content, timestamp } = data;
  if (!sessionId || !taskId || !messageId) return;

  const store = getStore();
  const tempId = `hitl-pending-${taskId}`;
  const messages = store.getMessages(sessionId);

  // Check if already have real ID
  if (messages.some((m) => m.id === messageId)) {
    console.log(
      "[AgentEventHandlers] HITL message already exists with real ID",
    );
    return;
  }

  // Check for temp ID to update
  if (messages.some((m) => m.id === tempId)) {
    store.updateMessage(sessionId, tempId, {
      id: messageId,
      status: TaskStatus.COMPLETED,
    });
    console.log(
      "[AgentEventHandlers] Updated HITL temp ID:",
      tempId,
      "->",
      messageId,
    );
  } else {
    // Get userMessageId from localStorage
    const taskData = getTaskData(taskId);
    const userMessageId = taskData?.userMessageId || taskData?.parentEntryId;

    store.upsertMessage(sessionId, {
      id: messageId,
      type: MessageType.ASSISTANT,
      content,
      timestamp: new Date(timestamp),
      status: TaskStatus.COMPLETED,
      userMessageId: userMessageId as string | undefined,
    });
    console.log("[AgentEventHandlers] Added new HITL message:", messageId);
  }

  // Cleanup localStorage
  localStorage.removeItem(`hitl-pending-${taskId}`);
}

/**
 * Handle hitl-action-clicked event
 * Updates clickedActions array on message
 */
export function handleHitlActionClicked(data: HitlActionClickedPayload): void {
  const { messageId, actionId, sessionId } = data;
  if (!messageId || !actionId) return;

  // Need sessionId to update store - get from active session if not provided
  const effectiveSessionId = sessionId || getStore().activeSessionId;
  if (!effectiveSessionId) {
    console.log("[AgentEventHandlers] No sessionId for hitl-action-clicked");
    return;
  }

  const store = getStore();
  const messages = store.getMessages(effectiveSessionId);
  const message = messages.find((m) => m.id === messageId);

  if (message) {
    const clickedActions = [...(message.clickedActions || []), actionId];
    store.updateMessage(effectiveSessionId, messageId, { clickedActions });
    console.log("[AgentEventHandlers] Updated clicked actions:", messageId);
  }
}

/**
 * Handle task-created event
 * Updates placeholder ID to taskId, adds activeTask to Zustand store
 * Creates localStorage entry for browser refresh recovery
 */
export function handleTaskCreated(data: TaskCreatedPayload): void {
  const { taskId, sessionId, clientMessageId, parentEntryId, userMessageId } =
    data;
  if (!taskId || !sessionId) return;

  const store = getStore();
  const messages = store.getMessages(sessionId);

  // Try to update placeholder by clientMessageId first
  if (clientMessageId) {
    const hasPlaceholder = messages.some((m) => m.id === clientMessageId);
    if (hasPlaceholder) {
      // Include taskId property for initSessionWithMerge lookup after navigation
      store.updateMessage(sessionId, clientMessageId, {
        id: taskId,
        taskId,
        userMessageId,
      });
      console.log(
        "[AgentEventHandlers] Updated placeholder by clientMessageId:",
        taskId,
      );
    }
  }

  // Fallback: try placeholder-{parentEntryId}
  const otherTabPlaceholderId = `placeholder-${parentEntryId}`;
  const hasOtherTabPlaceholder = messages.some(
    (m) => m.id === otherTabPlaceholderId && m.isLoading,
  );
  if (hasOtherTabPlaceholder) {
    // Include taskId property for initSessionWithMerge lookup after navigation
    store.updateMessage(sessionId, otherTabPlaceholderId, {
      id: taskId,
      taskId,
      userMessageId,
    });
    console.log("[AgentEventHandlers] Updated other-tab placeholder:", taskId);
  }

  // Add active task to Zustand store
  store.addActiveTask(sessionId, {
    taskId,
    status: TaskStatus.RUNNING,
    userMessageId,
    startedAt: Date.now(),
  });

  // Create localStorage entry for browser refresh recovery
  // Get projectId from socket manager (may be null if not yet initialized)
  const projectId = getClaudeSocketManagerSync()?.getProjectId();
  const taskData = {
    taskId,
    sessionId,
    projectId,
    status: TaskStatus.RUNNING,
    startedAt: new Date().toISOString(),
    completedAt: null,
    messages: [],
    userMessageId: userMessageId || parentEntryId,
  };
  setTaskData(taskId, taskData);
  console.log(
    "[AgentEventHandlers] Created task in localStorage:",
    taskId.slice(0, 8),
  );
}

/**
 * Handle task-queued event
 * Called when user sends message while container is paused
 * Updates placeholder to show "queued" status, task will process when container resumes
 */
export function handleTaskQueued(data: TaskQueuedPayload): void {
  const {
    taskId,
    sessionId,
    clientMessageId,
    parentEntryId,
    userMessageId,
    reason,
  } = data;
  if (!taskId || !sessionId) return;

  const store = getStore();
  const messages = store.getMessages(sessionId);

  // Try to update placeholder by clientMessageId first
  if (clientMessageId) {
    const hasPlaceholder = messages.some((m) => m.id === clientMessageId);
    if (hasPlaceholder) {
      store.updateMessage(sessionId, clientMessageId, {
        id: taskId,
        taskId,
        userMessageId,
        status: TaskStatus.QUEUED,
        isLoading: false,
        queuedReason: reason, // For UI to show "waiting for container"
      });
      console.log(
        "[AgentEventHandlers] Updated placeholder to queued by clientMessageId:",
        taskId,
      );
    }
  }

  // Fallback: try placeholder-{parentEntryId}
  const otherTabPlaceholderId = `placeholder-${parentEntryId}`;
  const hasOtherTabPlaceholder = messages.some(
    (m) => m.id === otherTabPlaceholderId && m.isLoading,
  );
  if (hasOtherTabPlaceholder) {
    store.updateMessage(sessionId, otherTabPlaceholderId, {
      id: taskId,
      taskId,
      userMessageId,
      status: TaskStatus.QUEUED,
      isLoading: false,
      queuedReason: reason,
    });
    console.log(
      "[AgentEventHandlers] Updated other-tab placeholder to queued:",
      taskId,
    );
  }

  // Add active task to Zustand store with QUEUED status
  store.addActiveTask(sessionId, {
    taskId,
    status: TaskStatus.QUEUED,
    userMessageId,
    startedAt: Date.now(),
    queuedReason: reason,
  });

  // Create localStorage entry for browser refresh recovery
  const projectId = getClaudeSocketManagerSync()?.getProjectId();
  const taskData = {
    taskId,
    sessionId,
    projectId,
    status: TaskStatus.QUEUED,
    queuedReason: reason,
    startedAt: new Date().toISOString(),
    completedAt: null,
    messages: [
      {
        id: taskId,
        type: MessageType.ASSISTANT,
        content: "",
        timestamp: new Date().toISOString(),
        status: TaskStatus.QUEUED,
        queuedReason: reason,
      },
    ],
    userMessageId: userMessageId || parentEntryId,
  };
  setTaskData(taskId, taskData);
  console.log(
    "[AgentEventHandlers] Created queued task in localStorage:",
    taskId.slice(0, 8),
    reason,
  );
}

/**
 * Handle task-paused event
 * Updates message status to paused
 */
export function handleTaskPaused(data: TaskPausedPayload): void {
  const { taskId, sessionId, errorCode, errorMessage } = data;
  if (!taskId) return;

  // Get sessionId from localStorage if not provided
  let effectiveSessionId = sessionId;
  if (!effectiveSessionId) {
    const taskData = getTaskData(taskId);
    effectiveSessionId = taskData?.sessionId as string;
  }
  if (!effectiveSessionId) return;

  const store = getStore();
  store.updateMessage(effectiveSessionId, taskId, {
    status: TaskStatus.PAUSED,
  });

  // Set paused task in store for UI to display ErrorRecoveryBanner
  store.setPausedTask(effectiveSessionId, {
    taskId,
    errorCode: errorCode || "UNKNOWN_ERROR",
    errorMessage: errorMessage || "An unknown error occurred",
  });

  console.log("[AgentEventHandlers] Updated task status to paused:", taskId);
}

// ============================================================
// New Handlers (Phase 2 - Socket Consolidation)
// ============================================================

/**
 * Handle connection state change
 */
export function handleConnectionStateChange(state: ConnectionState): void {
  getStore().setConnectionState(state);
}

/**
 * Handle chat-error event
 * For byoc_error type, stores in byocError for modal display and stops the task
 */
export function handleChatError(
  data: {
    message: string;
    code?: string;
    error?: string;
    type?: string;
    title?: string;
    taskId?: string;
    resetTime?: string;
  },
  sessionId: string,
): void {
  if (!sessionId) return;

  // Handle BYOC errors specially - show modal instead of inline error
  if (data.type === "byoc_error") {
    console.log("[AgentEventHandlers] BYOC error received:", data.error);
    const store = getStore();

    // Set error state for modal
    store.setByocError({
      sessionId,
      errorType: data.error || "oauth_refresh_failed",
      title: data.title,
      message: data.message,
      taskId: data.taskId,
      resetTime: data.resetTime,
    });

    // Stop the task - remove from active tasks so UI stops spinning
    if (data.taskId) {
      store.updateMessage(sessionId, data.taskId, {
        status: TaskStatus.COMPLETED,
      });
      store.removeActiveTask(sessionId, data.taskId);
      removeLocalStorageTask(data.taskId);
    }
    return;
  }

  // Regular errors
  getStore().setError(sessionId, {
    code: data.code || "UNKNOWN",
    message: data.message || data.error || "Unknown error",
  });
}

/**
 * Handle auto-commit-created event
 * Re-fetches slide manifest and triggers thumbnail regeneration (Replit checkpoint pattern)
 * Only regenerates thumbnails for slides whose files were changed in the commit.
 */
export function handleAutoCommitCreated(data: AutoCommitCreatedPayload): void {
  getStore().setAutoCommit({
    sessionId: data.sessionId,
    status: "created",
    commitId: data.commitId,
    commitSha: data.commitSha,
    filesCount: data.filesCount,
    message: data.message,
    timestamp: Date.now(),
  });

  // Slide thumbnail capture is now handled server-side by auto-commit subscriber.
  // Frontend just waits for 'slides-thumbnails-updated' WebSocket event.
}

/**
 * Handle slides-thumbnails-updated event from auto-commit subscriber.
 * Re-fetches slide manifest to pick up updated thumbnail fields.
 */
export function handleSlidesThumbnailsUpdated(): void {
  const devContainerId = _devContainerIdRef;
  const getIdToken = _getIdTokenRef;
  if (!devContainerId || !getIdToken) return;

  fetchSlideManifest({ devContainerId, getIdToken }).then(() => {
    useSlideStore.getState().bumpThumbnailVersion();
  });
}

/**
 * Handle auto-commit-failed event
 */
export function handleAutoCommitFailed(data: {
  sessionId: string;
  error: string;
}): void {
  getStore().setAutoCommit({
    sessionId: data.sessionId,
    status: "failed",
    error: data.error,
    timestamp: Date.now(),
  });
}

/**
 * Handle session-title-updated event
 * Updates store with session title - component subscribes and refreshes session list
 */
export function handleSessionTitleUpdated(data: {
  sessionId: string;
  title: string;
}): void {
  console.log(
    "[AgentEventHandlers] Session title updated:",
    data.sessionId,
    data.title,
  );
  getStore().setSessionTitleUpdated({
    sessionId: data.sessionId,
    title: data.title,
    timestamp: Date.now(),
  });
}

/**
 * Handle hitl-realtime event (store pending HITL in localStorage)
 */
export function handleHitlRealtime(data: {
  sessionId: string;
  taskId: string;
  content: string;
  timestamp: string;
}): void {
  const hitlKey = `hitl-pending-${data.taskId}`;
  localStorage.setItem(
    hitlKey,
    JSON.stringify({
      content: data.content,
      taskId: data.taskId,
      timestamp: data.timestamp,
    }),
  );
  console.log(
    "[AgentEventHandlers] Stored pending HITL for task:",
    data.taskId,
  );
}

/**
 * Handle chat-started event
 * Returns sessionId for component to use (component handles JOIN_SESSION emit)
 */
export function handleChatStarted(data: { id: string; sessionId?: string }): {
  sessionId: string;
} {
  const sessionId = data.id || data.sessionId || "";
  // Note: Session room tracking is done via sessionStorage/localStorage in component
  // No store state needed - Socket.io rooms are server-side only
  return { sessionId };
}
